import debounce from 'lodash/debounce'
import {defineStore} from 'pinia'
import {v4 as uuid} from 'uuid'
import {dashboard, getAvailableDashboardWidgets} from '@/utils/dashboard'

const debouncedSave = debounce((layouts, widgets) => {
    $lara.post(useNuxtApp().$apiRoute('spa.dashboard.store'), {body: {dashboard: {layouts, widgets}}})
}, 500)

function buildDashboardWidget(widget: Record<string, any>, editable: boolean, position = {}) {
    const widgetPosition = position as {x?: number, y?: number}

    return {
        ...widget,
        ...position,
        id: uuid(),
        settings: {},
        dragAllowFrom: ".drag-area",
        x: widgetPosition.x ?? widget.x ?? 0,
        y: widgetPosition.y ?? widget.y ?? 0,
        w: widget.width || 1,
        h: widget.height || 1,
        maxW: widget.maxWidth || 4,
        maxH: widget.maxHeight || 4,
        minW: widget.minWidth || 1,
        minH: widget.minHeight || 1,
        isDraggable: editable && (widget.draggable ?? true),
        isResizable: editable && (widget.resizable ?? true)
    }
}

type DashboardWidget = {
    id: string,
    type?: string,
    title?: string,
    settings: Record<string, any>,
    x: number,
    y: number,
    w: number,
    h: number,
    maxW: number,
    maxH: number,
    minW: number,
    minH: number,
    isDraggable: boolean,
    isResizable: boolean,
    [key: string]: any
}

type LayoutState = {
    isSidebarOpen: boolean,
    wikiSidebarOpen: boolean,
    isSubheaderOpen: boolean,
    currentModule: string | null,
    breakpoint: string | null,
    dashboardGrid: Array<any>,
    dashboardWidgets: Array<DashboardWidget>,
    dashboardGridResponsive: Record<string, any>,
    loadingDashboard: boolean
}

export const useLayoutStore = defineStore('layout', {
    state: (): LayoutState => ({
        isSidebarOpen: false,
        wikiSidebarOpen: false,
        isSubheaderOpen: false,
        currentModule: null,
        breakpoint: null,
        dashboardGrid: [], // stores the local and visible state of the dashboard
        dashboardWidgets: [], // stores the actual widgets that are saved in the database, important for updating on different devices
        dashboardGridResponsive: {}, // stores the responsive state of the dashboard
        loadingDashboard: false
    }),

    actions: {
        setSidebarOpen(isSidebarOpen: boolean) {
            this.isSidebarOpen = isSidebarOpen
        },
        setWikiSidebarOpen(isWikiSidebarOpen: boolean) {
            this.wikiSidebarOpen = isWikiSidebarOpen
        },
        setCurrentModule(currentModule: string | null) {
            this.currentModule = currentModule
        },
        async loadDashboard() {
            if (dashboard.editable) {
                const {$can} = useNuxtApp()
                const canAccessAdminWidgets = $can('admin', 'system')
                const availableWidgets = getAvailableDashboardWidgets(canAccessAdminWidgets)
                this.loadingDashboard = true
                const response = await $lara.get(useNuxtApp().$apiRoute('spa.dashboard.show'))
                this.loadingDashboard = false
                if (response.error) return
                this.dashboardGridResponsive = response?.layouts || {}
                this.dashboardWidgets = response?.widgets?.length
                    ? response.widgets.filter((widget: DashboardWidget) => !widget.adminOnly || canAccessAdminWidgets)
                    : availableWidgets.map((widget) => buildDashboardWidget(widget, true))
                this.dashboardGrid = this.dashboardGridResponsive[this.breakpoint || ''] || this.dashboardWidgets
                return
            }
            this.dashboardWidgets = dashboard.widgets.map((widget) => buildDashboardWidget(widget, false))

            this.dashboardGrid = this.dashboardWidgets
        },
        saveDashboard() {
            if (dashboard.editable) {
                debouncedSave(this.dashboardGridResponsive, this.dashboardWidgets)
            }
        },
        addWidget(widget: DashboardWidget) {
            const newWidget = buildDashboardWidget(widget, true, {x: 0, y: 0})

            this.dashboardWidgets.push(newWidget)
        },
        removeWidget(item: DashboardWidget) {
            this.dashboardWidgets = this.dashboardWidgets.filter((widget) => widget.id !== item.id)
        }
    }
})
